<template>
  <div class="flex w-full flex-col gap-6">
    <header class="flex flex-col gap-3 border-b pb-6">
      <div class="flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.2em] text-cyan-700 dark:text-cyan-300">
        <Building2 class="h-5 w-5" />
        Customer portal
      </div>
      <div class="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
        <div class="flex flex-col gap-2">
          <h1 class="text-3xl font-semibold tracking-tight">
            My companies
          </h1>
          <p class="max-w-3xl text-sm leading-6 text-muted-foreground">
            Choose a company to manage its holidays and support requests.
          </p>
        </div>
        <form class="flex w-full gap-2 lg:max-w-md" @submit.prevent="searchCompanies">
          <Input v-model="store.search" aria-label="Search my companies" placeholder="Search by name or account number" />
          <Button type="submit" variant="secondary" :loading="store.isLoading">
            <Search class="h-4 w-4" />
            Search
          </Button>
        </form>
      </div>
    </header>

    <Alert v-if="store.error" variant="destructive">
      <CircleAlert class="h-4 w-4" />
      <AlertTitle>Companies unavailable</AlertTitle>
      <AlertDescription>{{ store.error }}</AlertDescription>
    </Alert>

    <div class="flex items-center justify-between gap-4 text-sm text-muted-foreground">
      <span>{{ resultSummary }}</span>
      <Button v-if="store.search" type="button" variant="ghost" size="sm" @click="clearSearch">
        Clear search
      </Button>
    </div>

    <div v-if="store.isLoading && store.companies.length === 0" class="flex min-h-52 items-center justify-center gap-3 rounded-xl border bg-card text-sm text-muted-foreground">
      <LoaderCircle class="h-5 w-5 animate-spin" />
      Loading your companies…
    </div>

    <div v-else-if="store.companies.length > 0" class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      <CompanyCard v-for="company in store.companies" :key="company.uuid" :company="company" />
    </div>

    <div v-else-if="!store.error" class="rounded-xl border border-dashed bg-card px-6 py-14 text-center">
      <Building2 class="mx-auto h-9 w-9 text-muted-foreground" />
      <div class="flex flex-col gap-1">
        <h2 class="font-medium">
          {{ store.search ? 'No companies found' : 'No companies assigned' }}
        </h2>
        <p class="text-sm text-muted-foreground">
          {{ store.search ? 'Try a different search.' : 'Ask an administrator to assign your user account to a company.' }}
        </p>
      </div>
    </div>

    <nav v-if="store.pagination.lastPage > 1" class="flex items-center justify-center gap-3" aria-label="My company pages">
      <Button type="button" variant="outline" :disabled="store.pagination.currentPage === 1 || store.isLoading" @click="loadPage(store.pagination.currentPage - 1)">
        <ChevronLeft class="h-4 w-4" />
        Previous
      </Button>
      <span class="text-sm text-muted-foreground">Page {{ store.pagination.currentPage }} of {{ store.pagination.lastPage }}</span>
      <Button type="button" variant="outline" :disabled="store.pagination.currentPage === store.pagination.lastPage || store.isLoading" @click="loadPage(store.pagination.currentPage + 1)">
        Next
        <ChevronRight class="h-4 w-4" />
      </Button>
    </nav>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { Building2, ChevronLeft, ChevronRight, CircleAlert, LoaderCircle, Search } from 'lucide-vue-next'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import CompanyCard from '../components/CompanyCard.vue'
import { useUserCompanyStore } from '../stores/userCompanyStore.js'

const store = useUserCompanyStore()
const resultSummary = computed(() => `${store.pagination.total} ${store.pagination.total === 1 ? 'company' : 'companies'}`)

const loadPage = async page => {
  try {
    await store.fetchCompanies(page)
  } catch {
  }
}

const searchCompanies = () => loadPage(1)

const clearSearch = () => {
  store.search = ''
  loadPage(1)
}

onMounted(() => loadPage(1))
</script>
